import React, { useState, useCallback, useMemo, useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import Table from "../../../ui-components/table/table";
import ContentContainer from "../../../templates/content-container/contentContainer";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import CustomerServiceService from "../../../app/data/customer-service/customerServiceService";
import { CallTranscript } from "../../../app/data/customer-service/models";
import "./callTranscripts.scss";
import LabeledPhoneInput from "../../../ui-components/molecules/labeled-inputs/labeled-phone-input/labeledPhoneInput";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { PageResponseModel } from "app/data/common/models";
import { userSelector } from "slices/user/userSlice";

const formatCallType = (callType: string | null): string => {
  if (!callType) return "N/A";
  switch (callType) {
    case "OSD_OR_CLAIM":
      return "OSD or Claim";
    case "DIRECT_TRANSER": // TODO: remove this after we remove the direct transfer typo in backend
      return "Direct Transfer";
    default:
      return callType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
};

const customerServiceService = CustomerServiceService.getInstance();

const CallTranscripts: React.FC = () => {
  const userState = useSelector(userSelector);
  const history = useHistory();
  const location = useLocation();
  const [phoneNumberInput, setPhoneNumberInput] = useState<string>("");
  const [debouncedPhoneNumber, setDebouncedPhoneNumber] = useState<string>("");
  const [trackingNumberInput, setTrackingNumberInput] = useState<string>("");
  const [debouncedTrackingNumber, setDebouncedTrackingNumber] = useState<string>("");
  const [selectedTranscript, setSelectedTranscript] = useState<CallTranscript | null>(null);
  const pageSize = 20;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetPhoneNumber = useCallback(
    debounce((value: string) => {
      // Clean the phone number (remove all non-digits)
      const cleanedPhone = value.replace(/\D/g, "");
      setDebouncedPhoneNumber(cleanedPhone);
      // sync URL param
      const params = new URLSearchParams(location.search);
      if (cleanedPhone) {
        params.set("phoneNumber", cleanedPhone);
      } else {
        params.delete("phoneNumber");
      }
      history.replace({ pathname: location.pathname, search: params.toString() });
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetTrackingNumber = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setDebouncedTrackingNumber(trimmedValue);
      // sync URL param
      const params = new URLSearchParams(location.search);
      if (trimmedValue) {
        params.set("trackingNumber", trimmedValue);
      } else {
        params.delete("trackingNumber");
      }
      history.replace({ pathname: location.pathname, search: params.toString() });
    }, 500),
    []
  );

  // Track visit
  useEffect(() => {
    mixpanel.track("page_viewed", { page: "ai_transcripts" });
  }, [userState.activeTerminal?.id]);

  // Initialize from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramPhone = params.get("phoneNumber") || "";
    if (paramPhone) {
      // ensure digits only
      const cleaned = paramPhone.replace(/\D/g, "");
      setDebouncedPhoneNumber(cleaned);
      // format for display if 10 digits; otherwise just set raw
      if (cleaned.length === 10) {
        setPhoneNumberInput(cleaned.formatPhone());
      } else {
        setPhoneNumberInput(paramPhone);
      }
    }
    
    const paramTrackingNumber = params.get("trackingNumber") || "";
    if (paramTrackingNumber) {
      setDebouncedTrackingNumber(paramTrackingNumber);
      setTrackingNumberInput(paramTrackingNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: callTranscriptsData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData
  } = useInfiniteQuery<PageResponseModel<CallTranscript>, Error>({
    queryKey: [QUERY_KEYS.CALL_TRANSCRIPTS, debouncedPhoneNumber, debouncedTrackingNumber],
    queryFn: async ({ pageParam = 0 }) => {
      let info = await customerServiceService.getCallTranscripts(debouncedPhoneNumber, pageParam, pageSize, debouncedTrackingNumber);
      info.content = info.content.map((transcript) => {
        const dataCollected = transcript.dataCollected;
        if (!dataCollected) return transcript;
        try {
          const parsed = JSON.parse(dataCollected);
          return {
            ...transcript,
            dataCollected: parsed,
            trackingNumber: parsed["Tracking number"]?.value || ""
          };
        } catch (e) {
          return transcript;
        }
      });
      return info;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.last) return undefined;
      return lastPage.pageable.pageNumber + 1;
    },
    keepPreviousData: false,
    retry: false,
    refetchInterval: 3000
  });

  const transcripts = useMemo(() => callTranscriptsData?.pages.flatMap((page) => page.content) ?? [], [callTranscriptsData]);

  const columns = [
    {
      header: "Date",
      accessorKey: "dateCreated",
      cell: (info: any) => info.getValue().toUiDateTimeFormat(),
      size: 90
    },
    {
      header: "Phone Number",
      accessorKey: "phoneNumber",
      cell: (info: any) => {
        const phoneNumber = info.getValue();
        return phoneNumber ? phoneNumber.slice(2, 12).formatPhone() : "";
      },
      size: 110
    },
    {
      header: "Call Type",
      accessorKey: "callType",
      cell: (info: any) => formatCallType(info.getValue()),
      size: 120
    },
    {
      header: "Tracking number",
      accessorKey: "trackingNumber",
      size: 120,
    },
    {
      header: "Summary",
      accessorKey: "callSummaryTitle",
      size: 400
    }
  ];

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumberInput(value);
    debouncedSetPhoneNumber(value);
  };

  const handleTrackingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTrackingNumberInput(value);
    debouncedSetTrackingNumber(value);
  };

  return (
    <ContentContainer title="AI Call Transcripts">
      <div className="xgs-call-transcripts">
        <div className="xgs-call-transcripts__header">
          <div className="xgs-call-transcripts__filters">
            <LabeledPhoneInput
              type="text"
              value={phoneNumberInput}
              onValueChange={handlePhoneNumberChange}
              placeholder="Enter phone number"
              className="xgs-call-transcripts__phone-input"
              label="Phone number:"
              labelMode={LabelModes.column}
            />
            <LabeledTextInput
              type="text"
              value={trackingNumberInput}
              onChange={handleTrackingNumberChange}
              placeholder="Enter tracking number"
              className="xgs-call-transcripts__tracking-input"
              label="Tracking number:"
              labelMode={LabelModes.column}
            />
          </div>
        </div>
        <div className="xgs-call-transcripts__content">
          <Table
            data={transcripts}
            columns={columns}
            isLoading={isLoading || (isFetching && isPreviousData)}
            keepTableOnLoading
            infiniteScroll
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!callTranscriptsData?.pages.some(
              (page) => page.content.length !== pageSize
            )}
            onInfiniteScroll={() => fetchNextPage()}
            onRowClicked={(row: any) => setSelectedTranscript(row)}
            rowHeight={80}
            responsive
            cursorPointer
          />
        </div>
      </div>
      <SlideOutSidebar
        header="Call Transcript Details"
        show={!!selectedTranscript}
        onClose={() => setSelectedTranscript(null)}
        size="medium"
      >
        {selectedTranscript && (
          <div className="xgs-call-transcript-details">
            <div className="xgs-call-transcript-details__section">
              <h3 className="xgs-call-transcript-details__section-title">Call Information</h3>
              <div className="xgs-call-transcript-details__info-grid">
                <div className="xgs-call-transcript-details__info-item">
                  <span className="xgs-call-transcript-details__label">Date:</span>
                  <span className="xgs-call-transcript-details__value">
                    {new Date(selectedTranscript.dateCreated).toLocaleString()}
                  </span>
                </div>
                <div className="xgs-call-transcript-details__info-item">
                  <span className="xgs-call-transcript-details__label">Phone Number:</span>
                  <span className="xgs-call-transcript-details__value">
                    {selectedTranscript.phoneNumber ? selectedTranscript.phoneNumber.slice(2, 12).formatPhone() : "N/A"}
                  </span>
                </div>
                <div className="xgs-call-transcript-details__info-item">
                  <span className="xgs-call-transcript-details__label">Call Type:</span>
                  <span className="xgs-call-transcript-details__value">
                    {formatCallType(selectedTranscript.callType)}
                  </span>
                </div>
                <div className="xgs-call-transcript-details__info-item">
                  <span className="xgs-call-transcript-details__label">Tracking Number:</span>
                  <span className="xgs-call-transcript-details__value">
                    {selectedTranscript.trackingNumber || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="xgs-call-transcript-details__section">
              <h3 className="xgs-call-transcript-details__section-title">Call Summary</h3>
              <div className="xgs-call-transcript-details__summary">
                {selectedTranscript.callSummary || "No summary available"}
              </div>
            </div>

            <div className="xgs-call-transcript-details__section">
              <h3 className="xgs-call-transcript-details__section-title">Data Collected</h3>
              <div className="xgs-call-transcript-details__data-collected">
                {(() => {
                  try {
                    const dataCollected = selectedTranscript.dataCollected;
                    return Object.entries(dataCollected).map(([key, data]: [string, any]) => (
                      <div key={key} className="xgs-call-transcript-details__data-item">
                        <div className="xgs-call-transcript-details__data-label">{key}</div>
                        <div className="xgs-call-transcript-details__data-content">
                          <div className="xgs-call-transcript-details__data-row">
                            <span className="xgs-call-transcript-details__data-field">Value:</span>
                            <span className="xgs-call-transcript-details__data-value">
                              {data.value !== null ? String(data.value) : "N/A"}
                            </span>
                          </div>
                          {data.rationale && (
                            <div className="xgs-call-transcript-details__data-row">
                              <span className="xgs-call-transcript-details__data-field">Rationale:</span>
                              <span className="xgs-call-transcript-details__data-value">
                                {data.rationale}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ));
                  } catch (e) {
                    return <div>No data collected available</div>;
                  }
                })()}
              </div>
            </div>
          </div>
        )}
      </SlideOutSidebar>
    </ContentContainer>
  );
};

export default CallTranscripts;
